import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  type OnDestroy,
  type OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, type NavigationExtras, Router } from '@angular/router';
import { Observable, Subscription, map } from 'rxjs';
import type {
  Billing,
  BillingAddress,
} from '../../../server/models/billing.model';
import type { ProductSubscription } from '../../../server/models/product-subscription.model';
import type { StripeCharge } from '../../../server/models/stripe-charge.model';
import * as BillingGet from '../../../server/routes/billing/get';
import * as BillingPost from '../../../server/routes/billing/post';
import * as UpdateCardPost from '../../../server/routes/billing/update-card/post';
import {
  getPlanPrice,
  stripeAmountToString,
} from '../../../server/services/product.service';
import {
  type CountryData,
  type SubRegionData,
  auStates,
  countries,
  countryCodeFromTimeZoneFrontEnd,
  isBannedCountryCode,
  isEuCountry,
  usStates,
} from '../../../server/services/region.service';
import {
  generatePath,
  orgNameMaxLength,
} from '../../../server/services/utils.service';
import type { Org } from '../../../server/types/org-types';
import { AnalyticsService } from '../analytics.service';
import { AuthUserService } from '../auth-user.service';
import { BillingCancelDialogComponent } from '../billing-cancel-dialog/billing-cancel-dialog.component';
import { TitleService } from '../title.service';

@Component({
  selector: 'app-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss'],
  standalone: false,
})
export class BillingComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private authUserService = inject(AuthUserService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private titleService = inject(TitleService);
  private router = inject(Router);
  private analytics = inject(AnalyticsService);

  orgId: string;

  org: Org;
  existingBilling?:
    | (Omit<Billing, 'rawData'> & { rawData?: undefined })
    | undefined;
  existingProductSubscription:
    | (ProductSubscription & {
        startDateString: string;
        cancelDateString?: string;
      })
    | undefined;
  creditCard:
    | {
        brand: string;
        last4: string;
        expMonth: number;
        expYear: number;
      }
    | undefined;

  @ViewChild('countryInput', { static: false }) countryInput?: ElementRef;

  taxId = new FormControl('', {
    validators: [Validators.required],
    nonNullable: true,
  });
  name = new FormControl('', {
    validators: [Validators.required, Validators.maxLength(orgNameMaxLength)],
    nonNullable: true,
  });
  email = new FormControl('', {
    validators: [Validators.required, Validators.email],
    nonNullable: true,
  });
  address1 = new FormControl('', {
    validators: [Validators.required],
    nonNullable: true,
  });
  address2 = new FormControl('', {
    validators: [],
    nonNullable: true,
  });
  city = new FormControl('', {
    validators: [Validators.required],
    nonNullable: true,
  });
  postalCode = new FormControl('', {
    validators: [Validators.required],
    nonNullable: true,
  });
  subRegionControl = new FormControl('', {
    validators: [],
    nonNullable: true,
  });
  subRegionFreeFormControl = new FormControl('', {
    validators: [],
    nonNullable: true,
  });
  countryControl = new FormControl('', {
    validators: [],
    nonNullable: true,
  });

  countries = countries;
  filteredCountries: Observable<CountryData[]>;
  country?: CountryData;
  softCountry?: CountryData;
  subRegions: SubRegionData[] | undefined = undefined;
  filteredSubRegion: Observable<SubRegionData[]>;
  subRegion: SubRegionData | undefined = undefined;

  bannedCountry = false;

  currencyString = '';
  pricePerUserPerMonth = 0;
  pricePerUserPerYear = 0;

  state: 'loading' | 'ready' | 'error' = 'loading';
  editBillingDetails = false;
  editProductSubscription = false;
  submitSuccess = false;
  waiting = false;
  formErrorMessage?: string;
  disableSubmit = false;
  blockCountryPurchaseReason?: 'companiesOnly' | 'blockedCountry' = undefined;
  collectTaxId = false;
  taxIdName = 'VAT';
  postalCodeName: 'Postal code' | 'Zip code' | 'Postcode' = 'Postal code';

  intervalSelected: 'month' | 'year' = 'month';
  isBusiness = true;
  setIndividualAddress = false;

  activeUserCount: number;

  charges: (Omit<StripeCharge, 'rawData'> & {
    date: string;
    amountString: string;
  })[] = [];
  chargesColumns: string[] = ['date', 'amount', 'invoice'];

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Billing');
    this.orgId = this.route.snapshot.params.orgId;

    this.filteredCountries = this.countryControl.valueChanges.pipe(
      map((value) => this.countryFilter(value))
    );

    const path = generatePath<BillingGet.ReqParams>(
      '/api/orgs/:orgId/billing',
      { orgId: this.orgId }
    );
    this.http.get<BillingGet.ResBody>(path).subscribe({
      next: ({ billing, productSubscription, creditCard, charges }) => {
        this.existingBilling = billing;
        this.existingProductSubscription = productSubscription
          ? {
              ...productSubscription,
              startDateString: new Date(
                productSubscription.startAt
              ).toLocaleDateString(),
              cancelDateString: productSubscription.cancelAt
                ? new Date(productSubscription.cancelAt).toLocaleDateString()
                : undefined,
            }
          : undefined;
        this.creditCard = creditCard;
        this.charges = charges.map((c) => ({
          ...c,
          date: new Date(c.createdAt).toLocaleDateString(),
          amountString: stripeAmountToString({
            currency: c.currency,
            amount: c.amount,
          }),
        }));

        if (
          charges[0] &&
          this.route.snapshot.queryParams['notice'] === 'success'
        ) {
          let latestCharge:
            | (Omit<StripeCharge, 'rawData'> & {
                rawData?: undefined;
              })
            | undefined;
          charges.forEach((charge) => {
            if (!latestCharge || latestCharge.createdAt < charge.createdAt)
              latestCharge = charge;
          });
          if (latestCharge)
            this.analytics.purchase({
              value: latestCharge.amount,
              currency: latestCharge.currency,
              transactionId: latestCharge.stripeChargeId,
            });
        }

        this.state = 'ready';

        // If neither has ever been set, set both fields to be editable
        if (!this.existingBilling && !this.existingProductSubscription) {
          this.editBillingDetails = true;
          this.editProductSubscription = true;
        }

        const authUserOrgObj = this.authUserService.getAuthUserOrgObj({});
        this.org = authUserOrgObj.org;
        this.activeUserCount = this.org.users.filter(
          (u) => !u.archivedAt
        ).length;

        if (this.existingProductSubscription)
          this.intervalSelected = this.existingProductSubscription.interval;
        else this.intervalSelected = 'month';

        const countryCode =
          this.existingBilling?.countryCode ??
          countryCodeFromTimeZoneFrontEnd();
        const country = this.countries.find((c) => c.alpha3 === countryCode);
        if (country) this.setCountry(country);

        if (this.existingBilling) {
          if (this.existingBilling.tax)
            this.taxId.setValue(this.existingBilling.tax.value);

          const { name: billingName, email, address } = this.existingBilling;

          this.name.setValue(billingName);
          this.email.setValue(email);

          if (address) {
            const { line1, line2, city, postalCode, state } = address;
            if (line1) this.address1.setValue(line1);
            if (line2) this.address2.setValue(line2);
            if (city) this.city.setValue(city);
            if (postalCode) this.postalCode.setValue(postalCode);

            if (state) {
              if (
                this.existingBilling.countryCode === 'AUS' ||
                this.existingBilling.countryCode === 'USA'
              ) {
                this.subRegionControl.setValue(state);
                this.setSubRegion(state);
              } else {
                this.subRegionFreeFormControl.setValue(state);
              }
            }
          }
        } else {
          this.email.setValue(authUserOrgObj.user.email);
        }
      },
      error: (err) => {
        this.state = 'error';
      },
    });
  }

  showEditBilling() {
    this.editBillingDetails = true;
    const countryCode =
      this.existingBilling?.countryCode ?? countryCodeFromTimeZoneFrontEnd();
    const country = this.countries.find((c) => c.alpha3 === countryCode);
    if (country) setTimeout(() => this.setCountry(country), 1);
  }

  onSetBusiness(event) {
    this.calculateCountryTaxAndBlock();
  }

  private calculateCountryTaxAndBlock() {
    // Determine if the state is allowable for a payment
    if (this.country) {
      if (
        this.country.alpha3 === 'AUS' ||
        this.country.alpha3 === 'NZL' ||
        this.country.alpha3 === 'USA'
      ) {
        this.blockCountryPurchaseReason = undefined;
      } else if (
        this.country.alpha3 === 'GBR' ||
        isEuCountry(this.country.alpha3)
      ) {
        if (this.isBusiness) this.blockCountryPurchaseReason = undefined;
        else this.blockCountryPurchaseReason = 'companiesOnly';
      } else {
        this.blockCountryPurchaseReason = 'blockedCountry';
      }
    } else {
      // Hide errors !this.country
      this.blockCountryPurchaseReason = undefined;
    }

    this.collectTaxId = !!(
      this.country &&
      this.isBusiness &&
      (['AUS', 'NZL', 'GBR'].includes(this.country.alpha3) ||
        isEuCountry(this.country.alpha3))
    );

    this.taxIdName = !this.country
      ? 'VAT'
      : this.country.alpha3 === 'AUS'
      ? 'Australian Business Number'
      : this.country.alpha3 === 'NZL'
      ? 'GST Number'
      : 'VAT';

    this.postalCodeName = !this.country
      ? 'Postal code'
      : this.country.alpha3 === 'AUS' ||
        this.country.alpha3 === 'NZL' ||
        this.country.alpha3 === 'GBR'
      ? 'Postcode'
      : this.country.alpha3 === 'USA'
      ? 'Zip code'
      : 'Postal code'; // EU
  }

  countryDisplay(country: CountryData): string {
    return country.name;
  }

  private countryFilter(inputValue: string | CountryData): CountryData[] {
    if (typeof inputValue !== 'string') return []; // This is a selection event

    const value = inputValue.toLowerCase();

    if (!value) {
      this.setSoftCountry(undefined);
      this.setCountry(undefined);
      return this.countries;
    }

    let startsWith: CountryData[] = [];
    const includes: CountryData[] = [];

    this.countries.forEach((option) => {
      if (
        option.name.toLowerCase().startsWith(value) ||
        option.alpha2.toLowerCase().startsWith(value) ||
        option.alpha3.toLowerCase().startsWith(value) ||
        (value === 'uk' && option.alpha3 === 'GBR')
      ) {
        startsWith.push(option);
      } else if (
        option.name.toLowerCase().includes(value) ||
        option.alpha2.toLowerCase().includes(value) ||
        option.alpha3.toLowerCase().includes(value)
      ) {
        includes.push(option);
      }
    });

    // Put US or UK at top
    startsWith = [
      ...startsWith.filter((c) => c.alpha3 === 'GBR' || c.alpha3 === 'USA'),
      ...startsWith.filter((c) => c.alpha3 !== 'GBR' && c.alpha3 !== 'USA'),
    ];

    const filteredCountries = [...startsWith, ...includes];

    // Soft select countries under specific conditions
    this.setSoftCountry(undefined);
    if (
      value === 'us' ||
      value === 'usa' ||
      value === 'united states' ||
      value === 'united states of america'
    ) {
      this.setSoftCountry(this.countries.find((c) => c.alpha3 === 'USA'));
    }
    if (
      value === 'uk' ||
      value === 'gb' ||
      value === 'gbr' ||
      value === 'united kingdom'
    ) {
      this.setSoftCountry(this.countries.find((c) => c.alpha3 === 'GBR'));
    }
    if (value === 'nz') {
      this.setSoftCountry(this.countries.find((c) => c.alpha3 === 'NZL'));
    }

    const exactMatch = this.countries.find(
      (c) => c.name.toLowerCase() === value
    );
    if (exactMatch) this.setSoftCountry(exactMatch);

    if (filteredCountries.length === 0) {
      this.setSoftCountry(undefined);
      this.setCountry(undefined);
    }

    return filteredCountries;
  }

  countrySelected(event: MatAutocompleteSelectedEvent) {
    this.setCountry(event.option.value);
  }

  setCountry(country: CountryData | undefined) {
    const oldCountryCode = this.country?.alpha3;
    this.country = country;
    if (!country) {
      this.subRegions = undefined;
      this.subRegion = undefined;
    } else if (country.alpha3 !== oldCountryCode) {
      const countryCode = country.alpha3;
      this.subRegion = undefined;
      this.subRegions =
        countryCode === 'USA'
          ? usStates
          : countryCode === 'AUS'
          ? auStates
          : undefined;
    }

    // Because autocomplete does not work well, we need to wait until next tick and fill
    setTimeout(() => {
      if (country && this.countryInput?.nativeElement)
        this.countryInput.nativeElement.value = country.name;
    }, 1);

    this.bannedCountry =
      !!this.country && isBannedCountryCode(this.country?.alpha3);

    this.calculateCountryTaxAndBlock();

    if (country) {
      const planPrice = getPlanPrice(country.alpha3);
      this.pricePerUserPerMonth = planPrice.month;
      this.pricePerUserPerYear = planPrice.year;
      this.currencyString = planPrice.currencyString;
    }
  }

  setSoftCountry(country: CountryData | undefined) {
    this.softCountry = country;
  }

  blurCountrySelected() {
    this.countryFilter(this.countryInput?.nativeElement.value);
    if (this.softCountry) this.setCountry(this.softCountry);

    if (!this.country) this.countryControl.setErrors({});
    else this.countryControl.setErrors(null);
  }

  subRegionSelected() {
    const subRegionCode = this.subRegionControl.value;
    this.setSubRegion(subRegionCode);
  }

  setSubRegion(subRegionCode: string | undefined) {
    this.subRegion = this.subRegions?.find((s) => s.code === subRegionCode);
  }

  submit(): void {
    if (!this.country) this.countryControl.setErrors({});
    if (!this.country) return;

    if (this.name.errors) return;

    const orgId = this.orgId;

    let billing:
      | BillingPost.CreateBilling
      | BillingPost.UpdateBilling
      | undefined;
    if (this.editBillingDetails) {
      let address: BillingAddress | undefined = undefined;
      if (this.isBusiness || this.setIndividualAddress) {
        let state: string | undefined = undefined;
        if (this.subRegions) {
          // If it is a country with specified drop downs
          if (!this.subRegion) {
            this.subRegionControl.setErrors({});
          } else {
            state = this.subRegion.code;
          }
        } else {
          state = this.subRegionFreeFormControl.value;
        }

        address = {
          line1: this.address1.value,
          line2: this.address2.value,
          city: this.city.value,
          state,
          postalCode: this.postalCode.value,
        };
      }

      if (this.existingBilling) {
        billing = { _id: this.existingBilling._id };
        if (this.email.value !== this.existingBilling.email)
          billing.email = this.email.value;
        if (this.name.value !== this.existingBilling.name)
          billing.name = this.name.value;
        if (this.country.alpha3 !== this.existingBilling.countryCode)
          billing.countryCode = this.country.alpha3;
        if (this.isBusiness !== this.existingBilling.isBusiness)
          billing.isBusiness = this.isBusiness;
        if (this.taxId.value !== this.existingBilling.tax?.value)
          billing.taxId = this.taxId.value;
        if (address) billing.address = address;

        if (Object.keys(billing).length === 1) billing = undefined;
      } else {
        billing = {
          email: this.email.value,
          name: this.name.value,
          countryCode: this.country.alpha3,
          isBusiness: this.isBusiness,
          taxId: this.taxId.value,
          address,
        };
      }
    }

    const subscription:
      | BillingPost.CreateSubscription
      | BillingPost.UpdateSubscription
      | undefined =
      // No edits
      !this.editProductSubscription
        ? undefined
        : // No existing subscription
        !this.existingProductSubscription
        ? { interval: this.intervalSelected }
        : // Update existing subscription
        this.existingProductSubscription &&
          this.intervalSelected !== this.existingProductSubscription.interval
        ? {
            _id: this.existingProductSubscription._id,
            interval: this.intervalSelected,
          }
        : undefined;

    const body: BillingPost.ReqBody = { billing, subscription };
    const path = generatePath<BillingPost.ReqParams>(
      '/api/orgs/:orgId/billing',
      { orgId }
    );
    this.waiting = true;
    this.http.post<BillingPost.ResBody>(path, body).subscribe({
      next: ({ url }) => {
        this.waiting = false;
        if (url) window.open(url, '_blank');
        else {
          // Reset on successful update
          const navExtras: NavigationExtras = {
            queryParams: { reload: Math.round(Math.random() * 100000000) },
          };
          this.router.navigate(['/org', this.orgId, 'billing'], navExtras);
        }
      },
      error: (err) => {
        this.waiting = false;
        this.formErrorMessage = err.message;
      },
    });
  }

  launchCancelDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { orgId: this.orgId };
    const dialogRef = this.dialog.open(
      BillingCancelDialogComponent,
      dialogConfig
    );
    const dialogSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;
    });
    this.subscriptions.add(dialogSubscription);
  }

  updateCreditCard() {
    const body: UpdateCardPost.ReqBody = {};
    const path = generatePath<UpdateCardPost.ReqParams>(
      '/api/orgs/:orgId/billing/update-card',
      { orgId: this.orgId }
    );
    this.http.post<UpdateCardPost.ResBody>(path, body).subscribe({
      next: ({ url }) => {
        window.open(url, '_blank');
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
